import { useCallback, useEffect, useState } from "react";

import { refreshBrebReceivingKeys } from "@/shared/hooks/useBrebReceivingKey";
import { HttpApiService } from "@/shared/services/httpApi.service";
import {
  type BrebKeyTransferPlan,
  type BrebKeyTransferResult,
  BrebReceivingKeyService,
} from "@/shared/services/payments/brebReceivingKey.service";

export type BrebKeyTransferPlanState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "ready"; plan: BrebKeyTransferPlan }
  | { status: "error"; message: string };

export interface UseBrebKeyTransfer {
  plan: BrebKeyTransferPlanState;
  reloadPlan: () => void;
  transfer: (toOrganizationId: string, twoFactorCode: string) => Promise<BrebKeyTransferResult>;
}

interface Outcome {
  request: string;
  plan: BrebKeyTransferPlan | null;
  error: string | null;
}

// The plan is read again for every destination picked; an answer for an earlier pick is dropped.
export function useBrebKeyTransfer(
  keyId: string,
  fromOrganizationId: string,
  toOrganizationId: string | null,
): UseBrebKeyTransfer {
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [attempt, setAttempt] = useState(0);
  const request =
    toOrganizationId && toOrganizationId !== fromOrganizationId
      ? `${keyId}:${toOrganizationId}:${attempt}`
      : null;

  useEffect(() => {
    if (!request || !toOrganizationId) return;
    let cancelled = false;
    BrebReceivingKeyService.getTransferPlan(keyId, fromOrganizationId, toOrganizationId).then(
      (plan) => {
        if (!cancelled) setOutcome({ request, plan, error: null });
      },
      (error: unknown) => {
        if (!cancelled) {
          setOutcome({ request, plan: null, error: HttpApiService.getErrorMessage(error) });
        }
      },
    );
    return () => {
      cancelled = true;
    };
  }, [request, keyId, fromOrganizationId, toOrganizationId]);

  const reloadPlan = useCallback(() => setAttempt((value) => value + 1), []);

  const transfer = useCallback(
    async (destinationId: string, twoFactorCode: string) => {
      try {
        return await BrebReceivingKeyService.transferKey(
          keyId,
          fromOrganizationId,
          destinationId,
          twoFactorCode,
        );
      } finally {
        // A failure may still have moved it (a lost response), so no key view keeps trusting its cache.
        refreshBrebReceivingKeys();
      }
    },
    [keyId, fromOrganizationId],
  );

  let plan: BrebKeyTransferPlanState;
  if (!request) plan = { status: "idle" };
  else if (outcome?.request !== request) plan = { status: "loading" };
  else if (outcome.plan) plan = { status: "ready", plan: outcome.plan };
  else plan = { status: "error", message: outcome.error ?? "" };
  return { plan, reloadPlan, transfer };
}
